import { isIos, isStandalone } from "./platform";

/**
 * Home-screen installation, from the browser's side.
 *
 * Two things make this awkward enough to deserve its own module:
 *
 *  1. Chrome fires `beforeinstallprompt` once, early, and only if it decides
 *     the app is installable. It can arrive before React has mounted, so the
 *     listener is registered at module load and the event is kept. There is no
 *     way to ask for it later — miss it and there is no install button.
 *  2. iOS has no equivalent. Safari will not surface a prompt and cannot be
 *     asked to, so the only honest thing to do there is tell the user where
 *     the Share menu is.
 *
 * The captured event is single-use: once prompted, Chrome will not hand it over
 * again for some time, so the UI must stop offering.
 */

export type InstallState =
  /** Already running from the home screen. */
  | "installed"
  /** Chrome has offered us a prompt to show. */
  | "promptable"
  /** iOS, where installing is a manual gesture we can only describe. */
  | "ios-manual"
  /** Nothing useful to say — desktop, or a browser that will not install. */
  | "unsupported";

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/** The event Chrome fires; not in the DOM lib because it is not standard. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to state changes, in the shape `useSyncExternalStore` wants. */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getInstallState(): InstallState {
  if (installed || isStandalone()) return "installed";
  if (deferredPrompt) return "promptable";
  if (isIos()) return "ios-manual";
  return "unsupported";
}

/**
 * Show the browser's install prompt.
 *
 * The event is dropped before the dialog is even awaited, because it cannot be
 * reused whatever the user picks: accepting installs the app, and declining
 * means Chrome will not offer another prompt for a while. Either way the
 * banner should stop asking.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferredPrompt;
  if (!event) return "unavailable";

  deferredPrompt = null;
  notify();

  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppresses Chrome's own mini-infobar so ours is the only prompt.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}
